import { useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * useSubscription - Hook for subscribing to WebSocket topics
 * 
 * This hook manages topic subscriptions through the global WebSocket connection.
 * It automatically subscribes on mount and unsubscribes on unmount.
 * 
 * @param {string} topic - The topic to subscribe to (e.g., 'scan:12345', 'admin:queue')
 * @param {function} onMessage - Callback function to handle incoming messages
 * @param {object} options - Optional configuration
 * @param {boolean} options.enabled - Whether the subscription is enabled (default: true)
 * @returns {object} Subscription state and control functions
 */
export function useSubscription(topic, onMessage, options = {}) {
	const { enabled = true } = options;
	const { isConnected, send, registerMessageHandler } = useWebSocket();
	const isSubscribedRef = useRef(false);
	const onMessageRef = useRef(onMessage);

	// Keep the message handler reference up to date
	onMessageRef.current = onMessage;

	/**
	 * Subscribe to the topic
	 */
	const subscribe = useCallback(() => {
		if (!topic || !enabled || isSubscribedRef.current) {
			return;
		}

		const success = send({
			type: 'subscribe',
			topic: topic
		});

		if (success) {
			isSubscribedRef.current = true;
		}
	}, [topic, enabled, send]);

	/**
	 * Unsubscribe from the topic
	 */
	const unsubscribe = useCallback(() => {
		if (!topic || !isSubscribedRef.current) {
			return;
		}

		send({
			type: 'unsubscribe',
			topic: topic
		});

		isSubscribedRef.current = false;
	}, [topic, send]);

	/**
	 * Handle incoming messages for this topic
	 */
	const handleMessage = useCallback((message) => {
		if (onMessageRef.current) {
			onMessageRef.current(message);
		}
	}, []);

	// Register message handler
	useEffect(() => {
		if (!topic || !enabled) {
			return;
		}

		const unregister = registerMessageHandler(topic, handleMessage);
		return unregister;
	}, [topic, enabled, registerMessageHandler, handleMessage]);

	// A dropped socket takes every server-side subscription with it:
	// ScanProgressBroadcaster.removeClient() clears the client out of all topic
	// sets, and the reconnected socket is issued a new clientId. `subscribe` is
	// guarded on `!isSubscribedRef.current`, so leaving the flag set across a
	// reconnect meant the client never re-sent its subscribe frame and sat
	// listening to a topic the server no longer had it on - silently, forever,
	// until a full page reload. That is why a Heartbeat run showed 0 replies for
	// its entire duration while the database climbed past 700.
	//
	// Declared BEFORE the subscribe effect so that on a false -> true
	// transition the flag is already cleared when subscribe() is considered.
	useEffect(() => {
		if (!isConnected) {
			isSubscribedRef.current = false;
		}
	}, [isConnected]);

	// Subscribe when connected and enabled
	useEffect(() => {
		if (isConnected && enabled && topic && !isSubscribedRef.current) {
			subscribe();
		}
	}, [isConnected, enabled, topic, subscribe]);

	// Unsubscribe on unmount or when disabled
	useEffect(() => {
		return () => {
			if (isSubscribedRef.current) {
				unsubscribe();
			}
		};
	}, [unsubscribe]);

	// Also unsubscribe when disabled
	useEffect(() => {
		if (!enabled && isSubscribedRef.current) {
			unsubscribe();
		}
	}, [enabled, unsubscribe]);

	return {
		isSubscribed: isSubscribedRef.current,
		subscribe,
		unsubscribe,
	};
}

export default useSubscription;
